import { AuthServiceInterface } from '@services/AuthService/interface';
import {
  BitrixUser,
  FavoriteProduct,
  LoadBitrixUserQuery,
  LoadUserQueryResponse,
} from '@services/AuthService/query/loadUserQuery';
import { $error } from '@settings/errorContext';
import { graphQLClient } from '@settings/services/graphQLClient';
import { GraphQLClient } from '@settings/services/graphQLClient/GraphQLClient';
import { loggerFactory } from '@settings/services/logger';

import {
  AddFavoritesProductMutation,
  AddFavoritesProductMutationParams,
  AddFavoritesProductMutationResponse,
} from './mutations/AddFavoritesProductMutation';
import {
  DeleteArchivedProductMutation,
  DeleteArchivedProductMutationParams,
} from './mutations/DeleteArchivedProductMutation';
import {
  DeleteFavoritesProductMutation,
  DeleteFavoritesProductMutationParams,
  DeleteFavoritesProductMutationResponse,
} from './mutations/DeleteFavoritesProductMutation';
import {
  BitrixUserIndividualOffer,
  LoadBitrixUserIndividualOffersQuery,
  LoadBitrixUserIndividualOffersQueryResponse,
} from './query/loadUserIndividualOffersQuery';

class AuthService implements AuthServiceInterface {
  private readonly logger = loggerFactory().make(`AuthService`);
  private readonly client: GraphQLClient;

  constructor(token?: string) {
    this.client = graphQLClient(token);
  }

  async LoadUser(token?: string): Promise<BitrixUser | null> {
    if (!token) {
      return null;
    }

    try {
      const { BitrixUserProfile } = await this.client.Query<null, LoadUserQueryResponse>(
        new LoadBitrixUserQuery(),
        {}
      );

      this.logger.Debug(`Load bitrix user`, BitrixUserProfile);

      return BitrixUserProfile;
    } catch (e) {
      this.logger.Error(e);

      throw e;
    }
  }

  async LoadUserIndividualOffers(token?: string): Promise<BitrixUserIndividualOffer[]> {
    if (!token) {
      return null;
    }

    try {
      const { bitrixUserIndividualOffersList } = await this.client.Query<
        null,
        LoadBitrixUserIndividualOffersQueryResponse
      >(new LoadBitrixUserIndividualOffersQuery(), {});

      this.logger.Debug(`Load individual offers of bitrix user`, bitrixUserIndividualOffersList);

      return bitrixUserIndividualOffersList;
    } catch (e) {
      this.logger.Error(e);

      throw e;
    }
  }

  async AddFavoritesProduct(params: AddFavoritesProductMutationParams): Promise<FavoriteProduct> {
    try {
      const response = await this.client.Query<
        AddFavoritesProductMutationParams,
        AddFavoritesProductMutationResponse
      >(new AddFavoritesProductMutation(params), {});

      this.logger.Debug(`Added product to favorites`, response.data);

      return response.data;
    } catch (e) {
      this.logger.Error(e);
      $error.next(e);
    }
  }

  async DeleteFavoritesProduct(params: DeleteFavoritesProductMutationParams): Promise<boolean> {
    try {
      const response = await this.client.Query<
        DeleteFavoritesProductMutationParams,
        DeleteFavoritesProductMutationResponse
      >(new DeleteFavoritesProductMutation(params), {});

      this.logger.Debug(`Favorite product deleted`, response.data);

      return response.data;
    } catch (e) {
      this.logger.Error(e);
      $error.next(e);
    }
  }

  async DeleteArchivedProduct(params: DeleteArchivedProductMutationParams): Promise<boolean> {
    try {
      const response = await this.client.Query<
        DeleteArchivedProductMutationParams,
        DeleteFavoritesProductMutationResponse
      >(new DeleteArchivedProductMutation(params), {});

      this.logger.Debug(`Archived product deleted`, response.data);

      return response.data;
    } catch (e) {
      this.logger.Error(e);
      $error.next(e);
    }
  }
}

export default AuthService;
